/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductImageStyles } from "@stoneage/Overrides/Components/ProductImage";
import { ProductCardSelections } from "@stoneage/Overrides/Widgets/ProductList/ProductCardSelections";
import ProductListActions, {
    ProductListActionsStyles,
} from "@stoneage/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@stoneage/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@stoneage/Overrides/Widgets/ProductList/ProductListProductInformation";
import { verticalProductCardSharedStyles } from "@stoneage/XDL/Utilities/SharedStyles/productCards";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections &
    HasProduct & {
        product: ProductModel;
        isAuthenticated: boolean | "";
        imageDimensions?: number;
        view: "List" | "Grid";
        relatedProductsEventHandler?: (destination: string) => void;
    };

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;

    partNumbersAuthenticated: InjectableCss;
    partNumbersNonAuthenticated: InjectableCss;
    productInformationStyles: ProductListProductInformationStyles;
    productActionsStyles: ProductListActionsStyles;
    productImage: ProductImageStyles;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
            justify-content: space-between;
        `,
    },
    ...verticalProductCardSharedStyles,
};

const styles = productGridCardStyles;

const ProductCardGrid: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showPartNumbers,
    showTitle,
    showPrice,
    showAddToCart,
    isAuthenticated,
    imageDimensions,
    view,
    relatedProductsEventHandler,
}) => (
    <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
        <StyledWrapper {...styles.imageWrapper}>
            <ProductListProductImage
                {...({ imageDimensions } as any)}
                showImage={showImage}
                extendedStyles={styles.productImage}
                imageClickHandler={relatedProductsEventHandler}
            />
        </StyledWrapper>
        <ProductListProductInformation
            showAttributes={showAttributes}
            showAvailability={showAvailability}
            showPartNumbers={showPartNumbers}
            showTitle={showTitle}
            extendedStyles={{
                ...styles.productInformationStyles,
                partNumbersWrapper: isAuthenticated
                    ? styles.partNumbersAuthenticated
                    : styles.partNumbersNonAuthenticated,
            }}
            view={view}
            productDescriptionClickHandler={relatedProductsEventHandler}
        />
        <ProductListActions
            showPrice={showPrice}
            showCompare={showCompare}
            showAvailability={showAvailability}
            showAddToCart={showAddToCart}
            extendedStyles={styles.productActionsStyles}
            ctaHandler={relatedProductsEventHandler}
        />
    </StyledWrapper>
);

export default ProductCardGrid;
